import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AppNotification, NotificationsMeta } from '../models/notification.models';
import { NotificationsService } from './notifications.service';

@Injectable({
  providedIn: 'root',
})
export class NotificationsStoreService {
  private readonly notificationsService = inject(NotificationsService);

  private readonly notificationsSignal = signal<AppNotification[]>([]);
  private readonly unreadCountSignal = signal(0);
  private readonly metaSignal = signal<NotificationsMeta | null>(null);

  readonly notifications = this.notificationsSignal.asReadonly();
  readonly unreadCount = this.unreadCountSignal.asReadonly();
  readonly meta = this.metaSignal.asReadonly();

  async refresh(): Promise<void> {
    await this.loadPage(1);
  }

  async loadPage(page: number): Promise<void> {
    const response = await firstValueFrom(this.notificationsService.list(page));
    this.notificationsSignal.set(response.data);
    this.unreadCountSignal.set(response.meta.unread_count);
    this.metaSignal.set(response.meta);
  }

  async markRead(id: string): Promise<void> {
    const notification = this.notificationsSignal().find((item) => item.id === id);
    if (!notification || notification.read_at) {
      return;
    }

    const updated = await firstValueFrom(this.notificationsService.markRead(id));
    this.notificationsSignal.update((items) => items.map((item) => (item.id === id ? updated : item)));
    this.unreadCountSignal.update((count) => Math.max(0, count - 1));
  }

  async markAllRead(): Promise<void> {
    await firstValueFrom(this.notificationsService.markAllRead());
    this.notificationsSignal.update((items) =>
      items.map((item) => (item.read_at ? item : { ...item, read_at: new Date().toISOString() }))
    );
    this.unreadCountSignal.set(0);
  }
}
